import './index.less';
import logo from '@/assets/LOGO.svg';
import Icon from '@/components/iconfont/Icon.jsx';
import { useState } from 'react';
import { userService } from '@/api/index.js';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setShowSurveySuccess } from '@/store/slices/home.js';
import { useTranslation } from 'react-i18next';

const STEPS = {
  11: {
    title: 'As_Survey_UseTitle',
    options: [
      { label: 'As_Survey_UseOption1', value: 0 },
      { label: 'As_Survey_UseOption2', value: 1 },
      { label: 'As_Survey_UseOption3', value: 2 },
      { label: 'As_Survey_UseOption4', value: 3 },
    ],
  },
  12: {
    title: 'As_Survey_CompanyName1',
    placeholder: 'As_Survey_CompanyName2',
  },
  13: {
    title: 'As_Survey_SchoolName1',
    placeholder: 'As_Survey_SchoolName2',
  },
  21: {
    title: 'As_Survey_PlatformTitle',
    options: [
      { label: 'As_Survey_PlatformOption1', value: 0 },
      { label: 'As_Survey_PlatformOption2', value: 1 },
      { label: 'As_Survey_PlatformOption3', value: 2 },
      { label: 'As_Survey_PlatformOption4', value: 3 },
    ],
  },
  22: {
    title: 'As_Survey_PlatformSpecify',
    placeholder: 'As_Survey_PlatformSpecify',
  },
  31: {
    title: 'As_Survey_FeatureTitle',
    options: [
      { label: 'As_Survey_FeatureOption1', value: 0 },
      { label: 'As_Survey_FeatureOption2', value: 1 },
      { label: 'As_Survey_FeatureOption3', value: 2 },
    ],
  },
};

function Options({ options, optionValue, onOptionChange }) {
  const { t } = useTranslation();
  return (
    <ul>
      {options.map((item, index) => (
        <li
          key={index}
          className={`${item.value === optionValue && 'selected'}`}
          onClick={() => onOptionChange(item.value)}>
          {t(item.label)}
        </li>
      ))}
    </ul>
  );
}

export default function Survey() {
  const { t } = useTranslation();
  const [step, setStep] = useState(11);
  const { title, options, placeholder } = STEPS[step];
  const [params, setParams] = useState({
    purpose: null,
    institutionName: '',
    channel: null,
    otherChannelName: '',
    concernFunction: null,
  });
  const optionValue = (() => {
    if (step === 11) {
      return params.purpose;
    } else if (step === 21) {
      return params.channel;
    } else if (step === 31) {
      return params.concernFunction;
    }
  })();
  const onOptionChange = (value) => {
    let key = 'purpose';
    if (step === 11) {
      params.institutionName = '';
    } else if (step === 21) {
      key = 'channel';
      params.otherChannelName = '';
    } else if (step === 31) {
      key = 'concernFunction';
    }
    setParams({
      ...params,
      [key]: value,
    });
  };

  const extraInput = (() => {
    if (step === 12) {
      return params.institutionName;
    } else if (step === 13) {
      return params.institutionName;
    } else if (step === 22) {
      return params.otherChannelName;
    }
  })();
  const onExtraInputChange = (event) => {
    let key = 'institutionName';
    if (step === 22) {
      key = 'otherChannelName';
    }
    setParams({
      ...params,
      [key]: event.target.value,
    });
  };

  const showBack = step > 11;
  const showContinue = step < 31;
  const showSubmit = step === 31;
  const continueDisabled = (() => {
    if (step === 12 || step === 13 || step === 22) {
      return !extraInput;
    } else {
      return optionValue === null;
    }
  })();

  const handleBack = () => {
    let previous = 11;
    if (step === 21) {
      if (params.purpose === 0) {
        previous = 12;
      } else if (params.purpose === 2) {
        previous = 13;
      }
    } else if (step === 22) {
      previous = 21;
    } else if (step === 31) {
      if (params.channel === 3) {
        previous = 22;
      } else {
        previous = 21;
      }
    }
    setStep(previous);
  };

  const handleContinue = () => {
    let next = 21;
    if (step === 11) {
      if (params.purpose === 0) {
        next = 12;
      } else if (params.purpose === 2) {
        next = 13;
      }
    } else if (step === 21) {
      if (params.channel === 3) {
        next = 22;
      } else {
        next = 31;
      }
    } else if (step === 22) {
      next = 31;
    }
    setStep(next);
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleSubmit = () => {
    userService.submitSurvey(params).then(() => {
      navigate('/home', {
        replace: true,
      });
      dispatch(setShowSurveySuccess(true));
    });
  };

  return (
    <div className="survey-page">
      <img src={logo} alt="Talecast" />

      <section>
        <ul>
          <li className="filled"></li>
          <li className={`${step >= 21 && 'filled'}`}></li>
          <li className={`${step >= 31 && 'filled'}`}></li>
        </ul>

        <div className="card bg-base-100">
          <h4>{t(title)}</h4>

          {options && (
            <Options
              options={options}
              optionValue={optionValue}
              onOptionChange={onOptionChange}></Options>
          )}

          {placeholder && (
            <div className="extra-input">
              <p>{t(placeholder)}</p>
              <input
                type="text"
                className="input input-bordered input-primary w-full max-w-xs"
                defaultValue={extraInput}
                onChange={onExtraInputChange}
              />
            </div>
          )}

          <div className="actions">
            {showBack ? (
              <button className="btn btn-outline" onClick={handleBack}>
                <Icon name="icon-back"></Icon>
                {t('As_Survey_PlatformBack')}
              </button>
            ) : (
              <i />
            )}

            {showContinue && (
              <button
                className="btn btn-primary"
                disabled={continueDisabled}
                onClick={handleContinue}>
                {t('As_Survey_PlatformContinue')}
                <Icon name="icon-continue"></Icon>
              </button>
            )}

            {showSubmit && (
              <button
                className="btn btn-primary"
                disabled={optionValue === null}
                onClick={handleSubmit}>
                {t('As_Survey_FeatureSubmit')}
                <Icon name="icon-check-stroke"></Icon>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
